<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when invoked from a list of assets with a linked tag
column.  This component renders all assets properties available.  The primary purpose is to support audits.

Actions available on this component for authorized users and admins are {DELETE, MODIFY}.  ADD ASSET is a global
invoked from the list component.  

Revisions:
    03/03/2025      pblucas     initial creation from RWA

-->
<template>
    <!-- master container set size relative to viewport -->
    <div class="_master_container">
        <!-- establishes column based flex container -->
        <div class="_fieldset_flex_col">
            <div class="_major_property_container" style="order: 1;">
                <!-- row for asset owner/steward and uuid-->
                <p class="_p_item_label">Asset Steward:<span class="_p_item_text">{{ asset['steward'] }}</span></p>
                <p class="_p_item_label">Steward Netid:<span class="_p_item_text">{{ asset['steward-netid'] }}</span></p>
                <p class="_p_item_label">Owning Org:   <span class="_p_item_text">{{ asset['owner-org'] }}</span></p>         
                <p class="_p_item_label">UUID:         <span class="_p_item_text_diminished">{{ asset.uuid }}</span></p>         
            </div>
        </div>

        <!-- asset identifying information-->
        <div class="_fieldset_flex_row" >
            <div class="_fieldset_flex_col" >
                <p class="_p_group_container_label" style="order: 1">Asset Basic Information</p>
                <div class="_property_group_box" style="order: 2">
                    <div class="_assets_table">
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Tag/Identifying Number</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text"><b>{{asset.tag}}</b></p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Asset Type</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{ prettyType(asset['asset-type'])}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Serial Number</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['serial-no']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Description</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['description']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Manufacturer</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset.mfg}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Model</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset.model}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Other Identifying Marks</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['other-ids']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">IP Vers 4 Address</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['ipv4-address']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">IP Vers 6 Address</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['ipv6-address']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Data Source</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['source-code']}}</p>
                            </div>
                        </div>
                    
                    </div>
                </div>
                <p class="_p_group_container_label" style="order: 3;">State of Illinois Auditing Information</p>
                <div class="_property_group_box_audit" style="order:4">
                    
                    <div class="_assets_table">
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Audit Category</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text"><b>{{asset['audit-category']}}</b></p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Date of Last Audit</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{prettyDate(asset['date-last-audit'])}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Machine Name</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['machine-name']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Operating System Type</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['os-type']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Operating System Version</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['os-version']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Anti-virus Name</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['av-name']}}</p>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_col_50">
                                <p class="_p_item_label">Anti-virus Version</p>
                            </div>
                            <div class="_col_50">
                                <p class="_p_item_text">{{asset['av-version']}}</p>
                            </div>
                        </div>
                    </div>
                </div>      
            </div>
            <div class="_fieldset_flex_col">
                <!-- data classification -->
                <div class="_property_group_container" style="order: 1;">
                    <p class="_p_group_container_label">Information Security</p>
                    <div class="_property_group_box">
                        <div class="_assets_table">
                            <div class="_row">
                                <div class="_assets_check_label_table_cell">
                                    <p class="_p_item_label">Asset contains sensitive data</p>
                                </div>
                                <div class="_assets_check_table_cell">
                                    <div class="_checks"  v-if="asset['has-sensitive-data']"><img src="@/assets/check-green.png" width="10" /></div>
                                    <div class="_checks"  v-else><img src="@/assets/red-x.png" width="10" /></div> 
                                </div>           
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Classification of Data</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{asset['data-class']}}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="_property_group_container" style="order:2;">
                    <p class="_p_group_container_label">FabWeb+ Data</p>
                    <div class="_property_group_box">
                        <div class='_assets_table'>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Chart</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['chart'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Original Purchase Cost</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">${{asset['cost'].toFixed(2)}}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Property Category</p>
                                </div>
                                <div class="_col_50">   
                                    <p class="_p_item_text">{{ asset['category'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Origination ID (OTag)</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['otag'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Banner Location Code</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['location'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Banner Location Detail</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['location-detail'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Invoice Number</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['invoice-number'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">IT Data (reserved)</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['it-data'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <!-- area for the asset notes -->
                                <div class="_col_50">
                                    <p class="_p_item_label">Notes</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text_ellipsis">{{ asset['notes'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Original Item ID</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['orig-item-id'] }} </p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Purchase Date</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ prettyDate(asset['purchase-date']) }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Replacement Cycle (yrs)</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['replacement-cycle'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Asset Status</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['status'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Unit</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['unit'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <div class="_col_50">
                                    <p class="_p_item_label">Warranty Expiration Date</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">{{ asset['warranty-expiration'] }}</p>
                                </div>
                            </div>
                            <div class="_row">
                                <p class="_p_diminished_grn">S/L depreciation, 1 period residual</p>
                            </div>
                            <div class="_row">                         
                                <div class="_col_50">
                                    <p class="_p_item_label">Net Present Value (NPV)</p>
                                </div>
                                <div class="_col_50">
                                    <p class="_p_item_text">${{ calcNPV }}</p>
                                </div>
                            </div>
                        </div>      
                    </div>
                </div>
            </div>
        </div>
        <!-- auditing information -->
        <div class="_fieldset_flex_row">           
            <!-- Boolean configuration checks -->
            <div class="_fieldset_flex_col" style="order:1;">           
                <p class="_p_group_container_label" >Audit Configuration Checks</p>
                <div class="_property_group_box_audit">
                    <div class="_assets_table">
                        <div class="_row">
                            <div class="_assets_check_label_table_cell">
                                <p class="_p_item_check_label">Anti-virus Installed and Operating</p>
                            </div>
                            <div class="_assets_check_table_cell">
                                <div class="_checks" v-if="asset['av-installed']"><img src="@/assets/check-green.png" width="10" /></div>
                                <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                            </div>               
                        </div>
                        <div class="_row">
                            <div class="_assets_check_label_table_cell">
                                <p class="_p_item_check_label">Firewall Installed, Configured, and Operating</p>
                            </div>
                            <div class="_assets_check_table_cell">
                                <div class="_checks" v-if="asset['firewall-active']"><img src="@/assets/check-green.png" width="10" /></div>
                                <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                            </div>              
                        </div>
                        <div class="_row">
                            <div class="_assets_check_label_table_cell">
                                <p class="_p_item_check_label">User is Admin on Machine</p>
                            </div>
                            <div class="_assets_check_table_cell">
                                <div class="_checks" v-if="asset['user-is-admin']"><img src="@/assets/check-green.png" width="10" /></div>
                                <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                            </div>           
                        </div>
                        <div class="_row">
                            <div class="_assets_check_label_table_cell">
                                <p class="_p_item_check_label">Asset Identified as UIUC Property</p>
                            </div>
                            <div class="_assets_check_table_cell">
                                <div class="_checks" v-if="asset['identified-as-uiuc']"><img src="@/assets/check-green.png" width="10" /></div>
                                <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                            </div>               
                        </div>
                        <div class="_row">
                            <div class="_assets_check_label_table_cell">
                                <p class="_p_item_check_label">CrowdStrike Falcon Sensor Active</p>
                            </div>
                            <div class="_assets_check_table_cell">
                                <div class="_checks" v-if="asset['crowdstrike-active']"><img src="@/assets/check-green.png" width="10" /></div>
                                <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                            </div>           
                        </div>
                        <div class="_row">
                            <div class="_assets_check_label_table_cell">
                                <p class="_p_item_check_label">Disk is Encrypted</p>
                            </div>
                            <div class="_assets_check_table_cell">
                                <div class="_checks" v-if="asset['disk-encrypted']"><img src="@/assets/check-green.png" width="10" /></div>
                                <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                            </div>           
                        </div>
                        <div class="_row">
                            
                        </div>
                    </div>
                </div>      
            </div>
        </div>
    </div>
</template>

<script>
    import {ref, computed } from 'vue'
    //import Component from '@/components/component.vue'

    import useDateHelpers           from '@/composables/useDateHelpers'

    export default {
        components: {
        },
        props: [
            'asset'
        ],

        emits: [
        ],
    
        setup(props)    {

            // calculates MPV given purchase date, property category, cost
            // S/L depreciation gterminating with residual of 1 period
            const calcNPV = computed( () =>   {

                // check mandatory props            
                if(   (props.asset['purchase-date'] == "")
                    || (props.asset['cost'] == 0.00) 
                    || (props.asset['category'] == "")) return ""

                let currentDate = new Date()
                let purchaseDate = new Date(props.asset['purchase-date'])
                let elapsedMs = currentDate.getTime() - purchaseDate.getTime()
 
                //get greatest frational whole integer
                let periods = Math.floor(elapsedMs/(1000 * 60 * 60 * 24 * 365))     // number ms in year    
                let annualDepreciation = 0.0 
                let npv = 0.0
                switch (props.asset['category']) {
                    case '3-year':
                    case '3-year property':
                        annualDepreciation = props.asset['cost']/3.0
                        if(periods >= 3) npv = annualDepreciation
                        else npv = props.asset['cost'] - periods*annualDepreciation
                        break;

                    case '5-year':
                    case '5-year property':
                        annualDepreciation = props.asset['cost']/5.0
                        if(periods >= 5) npv = annualDepreciation
                        else npv = props.asset['cost'] - periods*annualDepreciation
                        break;

                    case '7-year':
                    case '7-year property':
                        annualDepreciation = props.asset['cost']/7.0
                        if(periods >= 7) npv = annualDepreciation
                        else npv = props.asset['cost'] - periods*annualDepreciation
                        break;

                    case '10-year':
                    case '10-year property':
                        annualDepreciation = props.asset['cost']/10.0
                        if(periods >= 10) npv = annualDepreciation
                        else npv = props.asset['cost'] - periods*annualDepreciation
                        break;
                 
                }
                if(isNaN(npv) || npv == 'undefined') return ""
                else return npv.toFixed(2)         
            })
                      
            return {calcNPV}
        },

        data() {


            return {

            }
        },
        methods:  {
            prettyDate(date)     {
                const {makePrettyDate}      =   useDateHelpers()
                return makePrettyDate(date)
            },
            // Function: converts status enumeration to more readable 
            prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            }

        },
        // hooks
        beforeMount() {

        },
        mounted() {

        },
        beforeUpdate() {


        }
    }
</script>

<style scoped>

    ._master_container   {
        width: 98vw;
        height: 90%;
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        margin: 5px auto;
        padding: 5px;  
    }
    ._fieldset_flex_row {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        width: 98%;
        margin: 5px auto;
        padding: 5px;  
    }
    ._fieldset_flex_col {
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: flex-start;
        width: 50%;
        margin: 5px 25px;
        padding: 5px;  
    }
    ._major_property_container {
        width: 100%;
        font-size: 0.9em;
        text-align: left;
        margin: 5px 25px;
        padding: 5px;    
    }
    ._property_group_container {
        margin: 0 5px;
        padding: 0;
    }
    ._property_group_container_split {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 5px 25px;
        padding: 5px;     }

    ._p_group_container_label {
        color: #13294B;
        font-weight: bold;
        font-size: 0.9em;
        margin: 10px 0 0 0;
        text-align: left;
        padding: 0;
    }
    ._p_item_label {
        color: #13294B;
        font-weight: bold;
        text-align: left;
        margin: 1px 0 0 0;
        padding: 0;
    }
    ._p_item_check_label {
        width: 100%;
        color: #13294B;
        font-weight: normal;
        text-align: left;
        margin: 1px 0 0 0;
        padding: 0; 
    }
    ._p_item_text {
        color: black;
        font-weight: normal;
        text-align: left;
        margin: 1px 0 0 0;
        padding: 0 3px;
    }
    ._p_item_text_ellipsis {
        color: black;
        font-weight: normal;
        text-align: left;
        text-overflow: elipsis;
        margin: 1px 0 0 0;
        padding: 0 3px;
    }
    ._p_item_text_diminished {
        color: black;
        font-weight: normal;
        font-size: 0.9em;
        text-align: left;
        padding: 0 3px;
    }
    ._p_diminished_grn {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 3px 0 0 0;
        padding: 2px 0 0 10px;
    }
    ._property_group_box {
        background: #BFC9D9;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        text-align: left;
    }
    ._property_group_box_audit {
        height: 90%;
        background: #f9da98;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        text-align: left;
        padding: 0 5px;
    }
    ._checks {
        width: 5%;
        text-align: center;
        margin: 0;
        padding: 0;
    }
    ._assets_table {
        width: 100%;
        display: table;
        padding: 5px 0;
    }
    ._row   {
        content: "";
        display: flex;
        flex-direction: row;
        align-content: left;
        justify-content: left;
        flex-wrap: no-wrap;
        clear: both;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        margin: 0;
        padding: 0;
    }
    /*
    ._row:nth-child(1) {
        display: table-row;
        min-width: 40px;
        text-align: center;
        margin: 0;
        font-size: 0.8em;
        font-weight: bold;
        color: blue;
    }
    */
    ._assets_check_label_table_cell {
        display: table-cell;
        min-width:70%;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px;
    }
    ._assets_check_table_cell {
        display: table-cell;
        margin: 0;
        padding: 2px 0 2px 10px;
    }
    ._col_50 {
        width: 50%;
        overflow: hidden;
        margin-top: 5px;
        padding: 2px 0 2px 10px;
    }

 
    @media screen and (max-width: 620px) {
        ._col_50 {
            width: 100%;
            margin-top: 0;
        }
        ._row {
            flex-wrap: wrap;
        }
    }
    @media screen and (max-width: 800px) {
        ._fieldset_flex_row, ._r2_split_container, 
        ._r3_split_container, ._property_group_container_split {
            flex-wrap: wrap;  
        }
    }

</style>